import type { ReactNode } from "react";
import { ExternalLink, MapPin, Navigation } from "lucide-react";
import type { PickupLocationPublic, PickupOptionsPublic } from "@gaole/shared";
import { formatPickupAvailabilitySummary } from "@gaole/shared";
import { PickupSchedulePicker } from "./PickupSchedulePicker";

export function StorePickupSection({
  pickupOptions,
  locationId,
  onLocationIdChange,
  scheduledAt,
  onScheduledAtChange,
}: {
  pickupOptions: PickupOptionsPublic;
  locationId: string;
  onLocationIdChange: (id: string) => void;
  scheduledAt: string;
  onScheduledAtChange: (iso: string) => void;
}) {
  const selectedLocation = pickupOptions.locations.find((l) => l.id === locationId);

  return (
    <div className="space-y-2">
      <h2 className="font-display text-xs font-semibold uppercase tracking-widest text-white/40">
        Store pickup
      </h2>
      <label className="block text-xs">
        <span className="mb-1 block text-white/50">Location</span>
        <select
          value={locationId}
          onChange={(e) => onLocationIdChange(e.target.value)}
          className="w-full rounded-xl border border-white/10 bg-surface px-3 py-2 text-sm text-white"
        >
          {pickupOptions.locations.map((loc) => (
            <option key={loc.id} value={loc.id}>
              {loc.name}
            </option>
          ))}
        </select>
      </label>
      {selectedLocation && (
        <PickupLocationDetails location={selectedLocation} />
      )}
      {selectedLocation && pickupOptions && (
        <PickupSchedulePicker
          options={pickupOptions}
          location={selectedLocation}
          value={scheduledAt}
          onChange={onScheduledAtChange}
        />
      )}
    </div>
  );
}

function PickupLocationDetails({ location }: { location: PickupLocationPublic }) {
  const hoursSummary = formatPickupAvailabilitySummary(location.availability);

  return (
    <div className="rounded-2xl border border-white/5 bg-surface p-3 text-xs text-white/70">
      <p>{location.address1}</p>
      {location.address2 && <p>{location.address2}</p>}
      <p>
        {location.postcode} {location.city}, {location.state}
      </p>
      {hoursSummary && <p className="mt-2 text-white/50">{hoursSummary}</p>}
      {location.instructions && <p className="mt-2 text-white/50">{location.instructions}</p>}
      {(location.wazeUrl || location.googleMapsUrl) && (
        <div className="mt-3 flex flex-wrap gap-2">
          {location.wazeUrl && (
            <MapLink href={location.wazeUrl} label="Waze" icon={<Navigation size={12} />} />
          )}
          {location.googleMapsUrl && (
            <MapLink href={location.googleMapsUrl} label="Google Maps" icon={<MapPin size={12} />} />
          )}
        </div>
      )}
    </div>
  );
}

function MapLink({
  href,
  label,
  icon,
}: {
  href: string;
  label: string;
  icon: ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-[11px] font-semibold text-white/80 transition-colors hover:border-brand-pink/40 hover:text-brand-pink"
    >
      {icon}
      {label}
      <ExternalLink size={10} className="opacity-60" />
    </a>
  );
}
